import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { adminCreateTool, adminUpdateTool, adminListTools } from '../../services/toolsApi';
import RecommendedToolsPicker from '../../components/RecommendedToolsPicker';

const AdminToolForm = () => {
  const { machineId, toolId } = useParams();
  const isEdit = !!toolId;
  const navigate = useNavigate();
  const { password } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [machineTools, setMachineTools] = useState([]);
  const [form, setForm] = useState({
    id: '',
    vending_machine_id: machineId,
    name_uk: '',
    name_ru: '',
    description_uk: '',
    description_ru: '',
    image: '',
    price_per_4hours: 0,
    price_per_day: 0,
    price_per_week: 0,
    price_per_night: 0,
    available: 1,
    maintenance: 0,
    slot: 0,
    sort_order: 0,
    recommended_tools: [],
    purchase_price: 0,
  });

  useEffect(() => {
    adminListTools(password, machineId).then((tools) => {
      setMachineTools(tools);
      if (isEdit) {
        const t = tools.find((x) => x.id === toolId);
        if (t) {
          setForm({
            id: t.id,
            vending_machine_id: machineId,
            name_uk: t.name.uk,
            name_ru: t.name.ru,
            description_uk: t.description.uk,
            description_ru: t.description.ru,
            image: t.image,
            price_per_4hours: t.pricePer4Hours,
            price_per_day: t.pricePerDay,
            price_per_week: t.pricePerWeek,
            price_per_night: t.pricePerNight,
            available: t.available ? 1 : 0,
            maintenance: t.maintenance ? 1 : 0,
            slot: t.slot,
            sort_order: t.sortOrder || 0,
            recommended_tools: t.recommendedToolIds || [],
            purchase_price: t.purchasePrice || 0,
          });
        }
      } else {
        // Auto-populate for new tool
        const nextSlot = tools.length > 0 ? Math.max(...tools.map((t) => t.slot)) + 1 : 1;
        const nextSortOrder = tools.length;
        const nums = tools.map((t) => { const m = t.id.match(/(\d+)$/); return m ? parseInt(m[1], 10) : 0; });
        const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : 1;
        const nextId = `tool-${String(nextNum).padStart(3, '0')}`;
        setForm((prev) => ({ ...prev, id: nextId, slot: nextSlot, sort_order: nextSortOrder }));
      }
      setLoading(false);
    });
  }, [isEdit, toolId, machineId, password]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : type === 'number' ? Number(value) : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      setShowConfirm(true);
    } else {
      doSave();
    }
  };

  const doSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      const payload = { ...form, recommended_tools: JSON.stringify(form.recommended_tools) };
      if (isEdit) {
        await adminUpdateTool(password, payload);
      } else {
        await adminCreateTool(password, payload);
      }
      navigate(`/admin/machines/${machineId}/tools`);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-3 border-dewalt border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-xl font-bold text-black mb-6">
        {isEdit ? 'Edit Tool' : 'New Tool'}
      </h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID {!isEdit && <span className="text-xs text-gray-400 font-normal">(авто, можна змінити)</span>}
            </label>
            <input name="id" value={form.id} onChange={handleChange} disabled={isEdit} required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt disabled:bg-gray-100" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
            <input name="image" value={form.image} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name (UK)</label>
            <input name="name_uk" value={form.name_uk} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name (RU)</label>
            <input name="name_ru" value={form.name_ru} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (UK)</label>
            <textarea name="description_uk" value={form.description_uk} onChange={handleChange} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt resize-vertical" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (RU)</label>
            <textarea name="description_ru" value={form.description_ru} onChange={handleChange} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt resize-vertical" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price (UAH)</label>
            <input type="number" name="purchase_price" value={form.purchase_price} onChange={handleChange} min={0} placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt" />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">4 Hours</label>
            <input type="number" name="price_per_4hours" value={form.price_per_4hours} onChange={handleChange} min={0} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
            <input type="number" name="price_per_day" value={form.price_per_day} onChange={handleChange} min={0} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Week</label>
            <input type="number" name="price_per_week" value={form.price_per_week} onChange={handleChange} min={0} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Night</label>
            <input type="number" name="price_per_night" value={form.price_per_night} onChange={handleChange} min={0} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-dewalt" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-end pb-1 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" name="available" checked={form.available === 1} onChange={handleChange} className="w-4 h-4" />
              <span className="text-sm text-gray-700">Available</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" name="maintenance" checked={form.maintenance === 1} onChange={handleChange} className="w-4 h-4" />
              <span className="text-sm text-amber-600">Maintenance</span>
            </label>
          </div>
        </div>

        {machineTools.filter((mt) => mt.id !== form.id).length > 0 && (
          <RecommendedToolsPicker
            allTools={machineTools.filter((mt) => mt.id !== form.id)}
            selected={form.recommended_tools}
            onChange={(ids) => setForm((prev) => ({ ...prev, recommended_tools: ids }))}
          />
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-dewalt border-none rounded-lg text-sm font-bold text-black cursor-pointer hover:bg-dewalt-hover disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/admin/machines/${machineId}/tools`)}
            className="px-6 py-2.5 bg-gray-100 border-none rounded-lg text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </form>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h2 className="text-lg font-bold text-black mb-2">Update tool?</h2>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to save changes to <span className="font-semibold text-black">{form.name_uk || form.id}</span>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 border-none rounded-xl text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200"
              >
                No, keep editing
              </button>
              <button
                onClick={doSave}
                className="flex-1 px-4 py-2.5 bg-dewalt border-none rounded-xl text-sm font-bold text-black cursor-pointer hover:bg-dewalt-hover"
              >
                Yes, update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminToolForm;
